import React from 'react';
import {fromJS, List} from 'immutable';
import moment from 'moment';
import {stats} from 'lib/StatsUtil';
import {pluralize} from 'lib/stringUtils';
import {renderDuration} from 'lib/timeUtils';
import {asOrderedMap} from 'lib/Immutable.utils';
import classroomStore from 'client/Classrooms/Classroom/ClassroomV2.store';

import SettingsTable from 'sg/Tables/SettingsTable/SettingsTable.react';
import DownloadCSV from 'sg/Tables/SettingsTable/DownloadCSV.react';
import {Column} from 'sg/Tables/MandarkTable/MandarkTable.react';
import {makeLinkRowProps} from 'sg/Tables/Table/table.utils';
import FlatBarChart from 'generic/FlatBarChart/FlatBarChart.react';
import PerformanceIndicator from 'generic/PerformanceIndicator/PerformanceIndicator.react';
import {RANGES} from '@albert-io/atomic/organisms/DatePicker/DatePicker.helpers';
import {ReportsLinkBanner} from 'client/Reports/ReportsLinkBanner.react';

import classroomInsightsStore from '../ClassroomInsights.store';

const csvColumns = fromJS(
  {
    Students: {
      'Last Name': 'student_last_name',
      'First Name': 'student_first_name'
    },
    'Time Spent (HH:MM:SS)': {
      'Time Spent (seconds)': 'student_time_spent'
    },
    Progress: {
      'Correct Attempts': 'student_count_questions_correct',
      'Incorrect Attempts': 'student_count_questions_incorrect',
      Unanswered: 'student_count_questions_unanswered'
    },
    'Correct Attempts': 'student_count_questions_correct',
    'Incorrect Attempts': 'student_count_questions_incorrect',
    Unanswered: 'student_count_questions_unanswered'
  },
  asOrderedMap
);

export default class StudentsPerformance extends React.Component {
  getName = (student) => student.getFullName({formal: true});

  getTimeSpent = (student) => {
    const timeSpentDuration = moment.duration(student.getMeta().getSumOfGuessesTimeElapsed());
    return renderDuration(timeSpentDuration, {style: 'hh:mm:ss'});
  };

  getProgress = (student) => {
    const correctGuesses = this.getCorrectAttempts(student);
    const totalGuesses = this.getTotalAttempts(student);
    const incorrectGuesses = this.getIncorrectAttempts(student);
    const unansweredGuesses = this.getUnanswered(student);
    const totalPotentialGuesses = correctGuesses + incorrectGuesses + unansweredGuesses;

    const performanceData = fromJS([
      {
        fillColorClass: 'classroom-insights__performance--correct',
        fillPercentage: stats.percentage(correctGuesses, totalPotentialGuesses),
        keyLabel: 'Correct'
      },
      {
        fillColorClass: 'classroom-insights__performance--incorrect',
        fillPercentage: stats.percentage(incorrectGuesses, totalPotentialGuesses),
        keyLabel: 'Incorrect'
      },
      {
        fillColorClass: 'classroom-insights__performance--unanswered',
        fillPercentage: stats.percentage(unansweredGuesses, totalPotentialGuesses),
        keyLabel: 'Not Answered'
      }
    ]);

    return (
      <div className='usage-report__performance'>
        <small>
          {totalGuesses} {pluralize('total attempt', totalGuesses)}
        </small>
        <FlatBarChart inline data={performanceData} />
      </div>
    );
  };

  getMastery = (student) => {
    const studentMeta = student.getMeta();
    const correctGuesses = studentMeta.getCountOfCorrectGuesses();
    const totalGuesses = studentMeta.getCountOfGuesses();

    return (
      <PerformanceIndicator
        gtgTag={student.getId()}
        numberCorrect={correctGuesses}
        total={totalGuesses}
        isUnavailable={totalGuesses === 0}
        small
      />
    );
  };

  getTotalAttempts = (student) => student.getMeta().getCountOfGuesses();

  getUnanswered = (student) => student.getMeta().getCountOfUnansweredQuestions();

  getCorrectAttempts = (student) => student.getMeta().getCountOfCorrectGuesses();

  getIncorrectAttempts = (student) => {
    const studentMeta = student.getMeta();
    return studentMeta.getCountOfGuesses() - studentMeta.getCountOfCorrectGuesses();
  };

  getCSVHeaders = (visibleColumns) => {
    const firstColumn = csvColumns.keySeq().first();

    const visibleColumnsWithFirst = visibleColumns.toList().unshift(firstColumn);

    const formattedColumns = csvColumns
      .filter((column, key) => visibleColumnsWithFirst.includes(key))
      .flatten()
      .reduce((acc, value, key) => acc.push(`${value}=${key}`), List());

    return encodeURIComponent(formattedColumns.join(','));
  };

  getStudentLinkProps() {
    return makeLinkRowProps(
      (student) =>
        `/classes/${classroomStore.getClassroomId()}` +
        `/insights/topic-drilldown/student/${student.getId()}` +
        `?attempt=${classroomInsightsStore.getAttemptNumber()}` +
        `&startDate=${classroomInsightsStore.getStartDate()}` +
        `&endDate=${classroomInsightsStore.getEndDate()}` +
        `&subjectId=${classroomInsightsStore.getSubjectId()}` +
        `&guideLevelId=${classroomInsightsStore.getGuideLevelId()}`
    );
  }

  render() {
    const classroomName = classroomStore.getActiveClassroom().getName();
    const assignmentName = classroomInsightsStore.getAssignmentName();
    const isSubjectReport = classroomInsightsStore.isSubjectReport();
    const csvTitle = isSubjectReport
      ? `${classroomName}-Insights-Students`
      : `${assignmentName}-Students`;

    const classroomId = classroomStore.getClassroomId();
    const subjectId = classroomInsightsStore.getSubjectId();
    const attemptNumber = classroomInsightsStore.getAttemptNumber();
    const guideLevelId = classroomInsightsStore.getGuideLevelId();
    const assignmentId = classroomInsightsStore.getAssignmentId();
    const [startDate] = new Date(classroomInsightsStore.getStartDate()).toISOString().split('T');
    const [endDate] = new Date(classroomInsightsStore.getEndDate()).toISOString().split('T');

    const queryParams = new URLSearchParams();
    queryParams.set('startDate', startDate);
    queryParams.set('range', RANGES.CUSTOM);
    queryParams.set('endDate', endDate);
    queryParams.set('attempt', attemptNumber);
    if (classroomInsightsStore.getReportType() === 'subject') {
      queryParams.set('subjectId', subjectId);
      if (guideLevelId) {
        queryParams.set('guideLevelId', guideLevelId);
      }
    } else {
      queryParams.set('assignmentId', assignmentId);
    }

    return (
      <div>
        <ReportsLinkBanner
          className='u-mar_2'
          link={`/reports/classrooms/${classroomId}/students?${queryParams.toString()}`}
        />
        <SettingsTable
          query={classroomInsightsStore.getStudentPerformanceQuery()}
          rowPropsFunc={isSubjectReport ? this.getStudentLinkProps() : null}
          storeName='StudentsPerformanceTableStore'
          searchFilters={classroomInsightsStore.getStudentsSearchFilters}
          optionsMenu={
            <DownloadCSV
              csvQuery={classroomInsightsStore.getStudentsPerformanceCSVQuery}
              tableStoreName='StudentsPerformanceTableStore'
              fileName={csvTitle}
              getCSVHeaders={this.getCSVHeaders}
            />
          }
        >
          <Column
            defaultSortParam
            heading='Students'
            sortBy='last_name'
            alwaysVisible
            content={(student) => <span data-gtu={student.getId()}>{this.getName(student)}</span>}
          />
          <Column
            heading='Time Spent (HH:MM:SS)'
            align='right'
            sortBy='meta.sum_of_guesses_time_elapsed'
            defaultVisible
            content={this.getTimeSpent}
          />
          <Column
            heading='Progress'
            align='center'
            sortBy='meta.count_of_correct_guesses'
            defaultVisible
            content={this.getProgress}
          />
          <Column heading='Mastery' align='center' defaultVisible content={this.getMastery} />
          <Column
            heading='Total Attempts'
            align='right'
            sortBy='meta.count_of_guesses'
            content={this.getTotalAttempts}
          />
          <Column
            heading='Unanswered'
            align='right'
            sortBy='meta.count_of_unanswered_questions'
            content={this.getUnanswered}
          />
          <Column
            heading='Correct Attempts'
            align='right'
            sortBy='meta.count_of_correct_guesses'
            content={this.getCorrectAttempts}
          />
          <Column heading='Incorrect Attempts' align='right' content={this.getIncorrectAttempts} />
        </SettingsTable>
      </div>
    );
  }
}
